import React, { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { brand, chartColors, neutral } from "../../theme/palette";
import {
  formatDecimal,
  formatDuration,
  formatNumber,
  hourlyDistribution,
  sessionTrend,
} from "../../utils/analyticsMetrics";
import { ChartCard, ChartTooltip, Legend, SegmentedControl, axisProps } from "./AnalyticsUI";
import { GRANULARITY_OPTIONS } from "./RevenueChart";

const AMBER = chartColors.warning;

const kwh = (v) => `${formatDecimal(v)} kWh`;

const ChargingAnalytics = ({ sessions, summary, range, granularity, onGranularityChange, isLoading, error }) => {
  const trend = useMemo(
    () => sessionTrend(sessions, range.fromDate, range.toDate, granularity),
    [sessions, range.fromDate, range.toDate, granularity]
  );
  const hours = useMemo(() => hourlyDistribution(sessions), [sessions]);
  const peak = useMemo(() => hours.reduce((a, h) => (h.sessions > a.sessions ? h : a), hours[0]), [hours]);

  const outcome = [
    { name: "Delivered energy", value: summary.sessionsWithEnergy, color: brand[600] },
    { name: "No energy delivered", value: summary.sessionsWithoutEnergy, color: AMBER },
  ];
  const zeroShare = summary.sessions ? summary.sessionsWithoutEnergy / summary.sessions : 0;
  const empty = !sessions?.length;
  const toggle = <SegmentedControl options={GRANULARITY_OPTIONS} value={granularity} onChange={onGranularityChange} />;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <ChartCard
        className="lg:col-span-2"
        title="Charging sessions"
        description="Sessions started per period, split by whether energy was delivered."
        actions={toggle}
        isLoading={isLoading}
        error={error}
        isEmpty={empty}
      >
        <div className="mb-3">
          <Legend items={[{ label: "Delivered energy", color: brand[600] }, { label: "No energy delivered", color: AMBER }]} />
        </div>
        <ResponsiveContainer width="100%" height={230}>
          <BarChart data={trend} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={neutral[200]} strokeDasharray="3 3" />
            <XAxis dataKey="label" {...axisProps} minTickGap={24} />
            <YAxis {...axisProps} width={40} allowDecimals={false} />
            <Tooltip cursor={{ fill: neutral[100] }} content={<ChartTooltip />} />
            <Bar dataKey="withEnergy" name="Delivered energy" stackId="s" fill={brand[600]} maxBarSize={28} />
            <Bar dataKey="withoutEnergy" name="No energy delivered" stackId="s" fill={AMBER} radius={[3, 3, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="Session outcome"
        description="Share of sessions that delivered energy."
        isLoading={isLoading}
        error={error}
        isEmpty={empty}
      >
        <div className="relative">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={outcome} dataKey="value" nameKey="name" innerRadius={58} outerRadius={80} paddingAngle={2} stroke="none" startAngle={90} endAngle={-270}>
                {outcome.map((o) => <Cell key={o.name} fill={o.color} />)}
              </Pie>
              <Tooltip content={<ChartTooltip formatters={{ value: formatNumber }} />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xl font-semibold tabular-nums text-fg">{formatNumber(summary.sessions)}</span>
            <span className="text-xs text-muted">sessions</span>
          </div>
        </div>
        <div className="mt-2 space-y-2">
          {outcome.map((o) => (
            <div key={o.name} className="flex items-center justify-between text-13">
              <span className="flex items-center gap-2 text-neutral-600">
                <span className="h-2 w-2 rounded-full" style={{ background: o.color }} />
                {o.name}
              </span>
              <span className="tabular-nums font-medium text-fg">
                {formatNumber(o.value)}
                <span className="ml-1.5 text-muted">{summary.sessions ? `${((o.value / summary.sessions) * 100).toFixed(1)}%` : ""}</span>
              </span>
            </div>
          ))}
        </div>
        {zeroShare > 0.1 && (
          <p className="mt-3 rounded-md bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
            {(zeroShare * 100).toFixed(0)}% of sessions delivered no energy. These are often failed starts worth checking.
          </p>
        )}
      </ChartCard>

      <ChartCard
        className="lg:col-span-2"
        title="Energy delivered"
        description="Total kWh per period, and average energy per session."
        isLoading={isLoading}
        error={error}
        isEmpty={empty}
      >
        <div className="mb-3 flex flex-wrap gap-x-8 gap-y-2">
          <div>
            <p className="text-xs font-medium text-muted">Avg energy per session</p>
            <p className="text-lg font-semibold tabular-nums text-fg">{kwh(summary.avgEnergyPerSession)}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-muted">Avg session duration</p>
            <p className="text-lg font-semibold tabular-nums text-fg">{formatDuration(summary.avgDurationSeconds)}</p>
          </div>
          <div className="ml-auto self-end">
            <Legend items={[{ label: "Energy (kWh)", color: neutral[800], line: true }, { label: "Avg per session", color: brand[400], line: true }]} />
          </div>
        </div>
        <ResponsiveContainer width="100%" height={210}>
          <LineChart data={trend} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={neutral[200]} strokeDasharray="3 3" />
            <XAxis dataKey="label" {...axisProps} minTickGap={24} />
            <YAxis yAxisId="total" {...axisProps} width={48} tickFormatter={(v) => formatNumber(v)} />
            <YAxis yAxisId="avg" orientation="right" {...axisProps} width={36} tickFormatter={(v) => formatDecimal(v)} />
            <Tooltip cursor={{ stroke: neutral[300], strokeDasharray: "3 3" }} content={<ChartTooltip formatters={{ energy: kwh, avgEnergy: kwh }} />} />
            <Line yAxisId="total" type="monotone" dataKey="energy" name="Energy" stroke={neutral[800]} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            <Line yAxisId="avg" type="monotone" dataKey="avgEnergy" name="Avg per session" stroke={brand[400]} strokeWidth={2} strokeDasharray="4 3" dot={false} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="When vehicles charge"
        description="Sessions by start hour, across the selected period."
        isLoading={isLoading}
        error={error}
        isEmpty={empty}
      >
        {peak?.sessions > 0 && (
          <p className="mb-3 text-13 text-neutral-600">
            Busiest hour: <span className="font-semibold text-fg">{peak.label}</span>{" "}
            <span className="text-muted">({formatNumber(peak.sessions)} sessions)</span>
          </p>
        )}
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={hours} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
            <XAxis dataKey="hour" {...axisProps} interval={5} tickFormatter={(h) => `${String(h).padStart(2, "0")}h`} />
            <YAxis hide allowDecimals={false} />
            <Tooltip
              cursor={{ fill: neutral[100] }}
              content={<ChartTooltip formatters={{ energy: kwh }} labelFormatter={(h) => `${String(h).padStart(2, "0")}:00 – ${String(h).padStart(2, "0")}:59`} />}
            />
            <Bar dataKey="sessions" name="Sessions" radius={[2, 2, 0, 0]}>
              {hours.map((h) => <Cell key={h.hour} fill={h.hour === peak?.hour ? brand[600] : brand[200]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
};

export default ChargingAnalytics;
